import { useRef, useState, useEffect, useCallback, memo, type FC } from "react";

interface VideoProgressBarProps {
  currentTime: number;
  duration: number;
  buffered: TimeRanges | null;
  onSeek: (time: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  className?: string;
}

export const VideoProgressBar: FC<VideoProgressBarProps> = memo(({
  currentTime,
  duration,
  buffered,
  onSeek,
  onDragStart,
  onDragEnd,
}) => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [, setHoverTime] = useState<number | null>(null);
  const [dragTime, setDragTime] = useState<number | null>(null);

  // Show dragTime while dragging, otherwise show currentTime
  const displayTime = isDragging && dragTime !== null ? dragTime : currentTime;
  const currentProgress = duration > 0 ? (displayTime / duration) * 100 : 0;

  // Calculate buffered segments
  const getBufferedSegments = () => {
    if (!buffered || duration === 0) return [];

    const segments: { start: number; end: number }[] = [];
    for (let i = 0; i < buffered.length; i++) {
      segments.push({
        start: (buffered.start(i) / duration) * 100,
        end: (buffered.end(i) / duration) * 100,
      });
    }
    return segments;
  };

  const calculateTime = useCallback(
    (clientX: number) => {
      if (!progressBarRef.current || duration === 0) return 0;

      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (clientX - rect.left) / rect.width;
      return Math.max(0, Math.min(pos * duration, duration));
    },
    [duration],
  );

  // Mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const time = calculateTime(e.clientX);
      setDragTime(time);
      setIsDragging(true);
      onDragStart?.();
      // Initial seek for preview
      onSeek(time);
    },
    [calculateTime, onDragStart, onSeek],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!progressBarRef.current || duration === 0) return;

      const time = calculateTime(e.clientX);
      setHoverTime(time);

      if (isDragging) {
        setDragTime(time);
        // Update preview while dragging
        onSeek(time);
      }
    },
    [isDragging, calculateTime, duration, onSeek],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverTime(null);
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const touch = e.touches[0];
      const time = calculateTime(touch.clientX);
      setDragTime(time);
      setIsDragging(true);
      onDragStart?.();
      // Initial seek for preview
      onSeek(time);
    },
    [calculateTime, onDragStart, onSeek],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      const time = calculateTime(touch.clientX);
      setDragTime(time);
      // Update preview while dragging
      onSeek(time);
    },
    [isDragging, calculateTime, onSeek],
  );

  // Global mouse/touch move and up handlers
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const time = calculateTime(e.clientX);
      setDragTime(time);
      // Update preview while dragging
      onSeek(time);
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        if (dragTime !== null) {
          // Small delay to ensure smooth transition
          setTimeout(() => {
            onSeek(dragTime); // Final seek when released
          }, 50);
          setDragTime(null);
        }
        setIsDragging(false);
        onDragEnd?.();
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      const time = calculateTime(touch.clientX);
      setDragTime(time);
      // Update preview while dragging
      onSeek(time);
    };

    const handleGlobalTouchEnd = () => {
      if (isDragging) {
        if (dragTime !== null) {
          // Small delay to ensure smooth transition
          setTimeout(() => {
            onSeek(dragTime); // Final seek when released
          }, 50);
          setDragTime(null);
        }
        setIsDragging(false);
        onDragEnd?.();
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove);
      document.addEventListener("touchend", handleGlobalTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [isDragging, dragTime, onSeek, calculateTime]);

  const bufferedSegments = getBufferedSegments();

  return (
    <div
      dir="ltr"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      className="relative h-6 flex justify-center items-center select-none"
    >
      <div
        ref={progressBarRef}
        className={`relative w-full ${
          isDragging ? "h-2" : "h-1"
        } bg-[#ffffff4d] rounded-full cursor-pointer group transition-all duration-200`}
      >
        {/* Buffered segments */}
        {bufferedSegments.map((segment, index) => (
          <div
            key={index}
            className="absolute h-full bg-[#ffffff80] rounded-full"
            style={{
              left: `${segment.start}%`,
              width: `${segment.end - segment.start}%`,
            }}
          />
        ))}

        {/* Current progress */}
        <div
          className="absolute h-full bg-white rounded-full z-10"
          style={{
            width: `${currentProgress}%`,
            transition: isDragging ? "none" : "width 0.1s linear",
          }}
        />

        {/* Drag indicator */}
        {isDragging && dragTime !== null && (
          <div
            className="absolute top-1/2 size-3 bg-white rounded-full transform -translate-y-1/2"
            style={{
              left: `${(dragTime / duration) * 100}%`,
              transform: "translate(-50%, -50%)",
            }}
          />
        )}
      </div>
    </div>
  );
});

VideoProgressBar.displayName = "VideoProgressBar";
